import { useCallback, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { desksEndpoints } from '@/api/endpoints';
import FormFieldset from '@/components/atoms/Fieldset/templates/FormFieldset';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import useDesks from '@/hooks/useDesks';
import { getPermissions } from '@/redux/permissions/selectors';
import { Desk } from '@/shared/types/desk';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { submitWithTrimming } from '@/util/util';
import { LENGTH_XS } from '@/util/validation/constants';
import { errorMessage } from '@/util/validation/helpers';
import { deskRules } from '@/util/validation/rules/desk-rules';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

type Form = { name: string };

interface InformationProps {
  isLoading?: boolean;
  desk?: Desk;
}

const InformationFieldset = ({ isLoading = false, desk }: InformationProps) => {
  const { t } = useTranslation();

  const desk_id = desk?.desk_id;
  const { updateDesk, updateStatus } = useDesks(desk_id);

  const nameSchema = yup.object({
    name: createNameFieldSchema({ getEndpoint: desksEndpoints.checkName, excludeName: desk?.name }),
  });

  const formMethods = useForm<Form>({
    mode: 'onChange',
    resolver: yupResolver(nameSchema) as Resolver<Form>,
  });
  const {
    control,
    reset,
    formState: { errors, defaultValues },
  } = formMethods;

  // Not `isDirty` — the debounced name check makes react-hook-form drop that update.
  const currentName = useWatch({ control, name: 'name' });
  const hasChanges = (currentName ?? '').trim() !== (defaultValues?.name ?? '');

  const canWrite = useSelector((state: RootState) => getPermissions(state, 'desks', actions.WRITE));

  const onSubmit = useCallback<(form: Form) => void>(
    (form) => {
      updateDesk({ ...form, desk_id: desk?.desk_id });
    },
    [desk?.desk_id, updateDesk]
  );

  useEffect(() => {
    if (desk?.desk_id) {
      reset({ name: desk?.name });
    }
  }, [desk?.name, reset, desk?.desk_id]);

  return (
    <FormFieldset
      title={t('environments.environment_name')}
      primaryButton={{
        children: t('common.save'),
        isLoading: updateStatus === 'pending',
        disabled: !hasChanges || updateStatus === 'pending',
      }}
      onSubmit={submitWithTrimming(formMethods, onSubmit)}
      isLoading={isLoading}
      helpText={t('validation.less_than', { 0: LENGTH_XS })}
      className="mb-8"
    >
      <div className="space-y-2">
        <Label htmlFor="name">{t('field.name')}</Label>
        <Controller
          control={control}
          name="name"
          rules={deskRules(t).name}
          render={({ field }) => (
            <Input id="name" readOnly={!canWrite} {...field} value={field.value ?? ''} />
          )}
        />
        {errors.name && (
          <p className="text-sm text-destructive">
            {errorMessage({ field: errors.name, maxLength: deskRules(t).name.maxLength })}
          </p>
        )}
      </div>
    </FormFieldset>
  );
};

export default InformationFieldset;
